import { useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { useStore } from "@/context/StoreContext";
import { ORIGENS, origemLabel } from "@/lib/constants";
import { BACKEND_URL } from "@/lib/api";
import { Page, Empty, Modal } from "@/components/ui-z";
import ApiDocsModal from "@/components/ApiDocsModal";

const SAMPLE = `{
  "solicitante": "Maria Souza",
  "contato": "+55 11 99999-0000",
  "titulo": "Erro ao emitir nota",
  "descricao": "Sistema retorna erro 500 ao emitir NF",
  "conversation_id": "wa-chat-8812"
}`;

// URL de retorno: para onde a API avisa quando o atendente responde um chamado desta integração
// (ex.: webhook do n8n que posta a resposta na thread do Slack). Trocar a URL não muda o token
const ReturnUrlModal = ({ w, onClose }) => {
  const { updateWebhookUrl } = useStore();
  const modal = useRef(null);
  const [url, setUrl] = useState(w.url_destino || "");
  const save = async (e) => {
    e?.preventDefault();
    if (await updateWebhookUrl(w.id, url.trim())) {
      toast.success(url.trim() ? "URL de retorno salva" : "URL de retorno removida");
      modal.current.hide();
    }
  };
  return (
    <Modal ref={modal} testId="webhook-url-modal" onClose={onClose}
      title={<><i className="fas fa-reply mr-2" />URL de retorno · {w.nome}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button><button type="button" data-testid="webhook-url-save" className="btn btn-primary" onClick={save}>Salvar</button></>}>
      <form onSubmit={save}>
        <p className="text-sm">
          Quando o atendente responde um chamado desta integração (mensagem que não é nota interna), a API envia um POST para
          esta URL com o texto pronto e o cabeçalho <code>X-Zitec-Token</code> (o token desta integração), para quem recebe
          conferir a origem. No Slack, é o webhook <code>portal-retorno</code> do workflow do n8n.
        </p>
        <input data-testid="webhook-url-input" className="form-control" placeholder="https://n8n.zitec.ai/webhook/portal-retorno" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
        <small className="form-text text-muted">Deixe vazio para não enviar as respostas para lugar nenhum.</small>
      </form>
    </Modal>
  );
};

export default function Webhooks() {
  const { webhooks, createWebhook, deleteWebhook, simulateInbound } = useStore();
  const [f, setF] = useState({ nome: "", plataforma: "whatsapp", url_destino: "", ativo: true });
  const [sel, setSel] = useState("");
  const [payload, setPayload] = useState(SAMPLE);
  const [docs, setDocs] = useState(false);
  const [urlOf, setUrlOf] = useState(null);
  const base = `${BACKEND_URL}/api/webhooks/inbound/`;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome) return toast.error("Informe um nome");
    const w = await createWebhook(f);
    if (!w) return;
    setSel(w.token);
    setF({ ...f, nome: "", url_destino: "" });
    toast.success("Integração criada");
  };

  const test = async () => {
    let body;
    try { body = JSON.parse(payload); } catch { return toast.error("JSON inválido"); }
    const t = await simulateInbound(sel, body);
    if (t) toast.success(`Chamado ${t.codigo} criado via webhook`);
  };

  return (
    <Page title="Integrações & Rotas de API" crumbs={[["Gestão"], ["Webhooks"]]}>
      <div className="row">
        <div className="col-lg-4">
          <div className="card card-primary">
            <div className="card-header"><h3 className="card-title"><i className="fas fa-plug mr-1" />Nova integração</h3></div>
            <form onSubmit={submit} data-testid="new-webhook-form">
              <div className="card-body">
                <div className="form-group">
                  <label htmlFor="wh-name">Nome</label>
                  <input id="wh-name" data-testid="webhook-name-input" className="form-control" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="wh-platform">Plataforma</label>
                  <select id="wh-platform" data-testid="webhook-platform-select" className="form-control" value={f.plataforma} onChange={(e) => setF({ ...f, plataforma: e.target.value })}>{ORIGENS.filter((o) => o.key !== "portal").map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</select>
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="wh-url">URL de retorno (opcional)</label>
                  <input id="wh-url" data-testid="webhook-url-input" className="form-control" placeholder="https://..." value={f.url_destino} onChange={(e) => setF({ ...f, url_destino: e.target.value })} />
                </div>
              </div>
              <div className="card-footer">
                <button data-testid="webhook-submit-btn" type="submit" className="btn btn-primary">Criar integração</button>
              </div>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Integrações ({webhooks.length})</h3>
              <div className="card-tools">
                <button type="button" data-testid="api-docs-btn" className="btn btn-tool" onClick={() => setDocs(true)}><i className="fas fa-book mr-1" />Como integrar</button>
              </div>
            </div>
            <div className="card-body table-responsive p-0">
              {webhooks.length === 0 ? <Empty testId="webhooks-empty" text="Nenhuma integração configurada." /> : (
                <table className="table table-striped table-valign-middle" data-testid="webhooks-list">
                  <thead><tr><th>Nome</th><th>Plataforma</th><th>Endpoint</th><th /></tr></thead>
                  <tbody>
                    {webhooks.map((w) => (
                      <tr key={w.id}>
                        <td>{w.nome}</td>
                        <td><span className="badge bg-purple">{origemLabel(w.plataforma)}</span></td>
                        <td>
                          <code className="text-sm" style={{ wordBreak: "break-all" }}>POST {base}{w.token}</code>
                          <div className="text-sm text-muted mt-1" data-testid={`webhook-return-${w.id}`} style={{ wordBreak: "break-all" }}>
                            <i className="fas fa-reply mr-1" />Retorno: {w.url_destino ? <code>{w.url_destino}</code> : <em>não configurado</em>}
                          </div>
                        </td>
                        <td className="text-right text-nowrap">
                          <button type="button" data-testid={`webhook-url-${w.id}`} className="btn btn-default btn-sm mr-1" title="URL de retorno (respostas do portal)" onClick={() => setUrlOf(w)}><i className="fas fa-reply" /></button>
                          <button type="button" data-testid={`webhook-copy-${w.id}`} className="btn btn-default btn-sm mr-1" title="Copiar URL" onClick={() => { navigator.clipboard?.writeText(base + w.token); toast("URL copiada"); }}><i className="fas fa-copy" /></button>
                          <button type="button" data-testid={`webhook-delete-${w.id}`} className="btn btn-danger btn-sm" title="Remover" onClick={() => deleteWebhook(w.id)}><i className="fas fa-trash" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card card-info">
        <div className="card-header"><h3 className="card-title"><i className="fas fa-paper-plane mr-1" />Testar payload (chama a API real)</h3></div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 form-group">
              <label htmlFor="sim-webhook">Integração</label>
              <select id="sim-webhook" data-testid="simulator-webhook-select" className="form-control" value={sel} onChange={(e) => setSel(e.target.value)}>
                <option value="">Selecione...</option>{webhooks.map((w) => <option key={w.id} value={w.token}>{w.nome}</option>)}
              </select>
              <small className="form-text text-muted">
                Envia o JSON para <code>/api/webhooks/inbound/&#123;token&#125;</code> e cria um chamado de verdade.
                Campos, envio de arquivos e exemplos em cURL: <a href="#docs" data-testid="api-docs-link" onClick={(e) => { e.preventDefault(); setDocs(true); }}>documentação da integração</a>.
              </small>
            </div>
            <div className="col-md-8 form-group">
              <label htmlFor="sim-payload">JSON</label>
              <textarea id="sim-payload" data-testid="simulator-payload-input" rows={8} className="form-control" style={{ fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace" }} value={payload} onChange={(e) => setPayload(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="card-footer">
          <button type="button" data-testid="simulator-send-btn" className="btn btn-info" disabled={!sel} onClick={test}><i className="fas fa-paper-plane mr-1" />Enviar payload</button>
        </div>
      </div>
      {docs && <ApiDocsModal baseUrl={base} token={sel} onClose={() => setDocs(false)} />}
      {urlOf && <ReturnUrlModal w={urlOf} onClose={() => setUrlOf(null)} />}
    </Page>
  );
}
